import React, { useState, useEffect, useMemo } from 'react';
import {
  Alert, Button, Card, Columns03, Download01, EmptyState, FilterBar, Pagination, SearchLg, Table, TextInput,
} from '@opendate-app/setlist';
import ReportingPageLayout from './ReportingPageLayout';
import TableSettings from '../../shared/components/TableSettings';
import useReportingPage from '../hooks/useReportingPage';
import '../styles/reportingStyles.css';

const axios = require('axios').default;
const qs = require('qs');

const noop = () => {};
const PER_PAGE = 25;
const PINNED_COLUMN = 'donor_name';
const DEFAULT_COLUMNS = [PINNED_COLUMN, 'gift_date', 'amount', 'fair_market_value', 'deductible_amount', 'fund', 'campaign', 'email'];
const FILTER_IDS = ['fund_id', 'giving_campaign_id', 'pledged', 'payment_type'];

export const buildThankYouFilters = (activeFilters) => {
  const params = {};
  FILTER_IDS.forEach((key) => {
    if (activeFilters[key]?.length) params[key] = activeFilters[key];
  });
  if (activeFilters.pledged?.length === 1) params.pledged = activeFilters.pledged[0];
  else delete params.pledged;
  return params;
};

export const buildFilterDefinitions = (filters) => [
  { key: 'fund_id', label: 'Fund', options: filters.funds.map((fund) => ({ value: String(fund.id), label: fund.name })) },
  { key: 'giving_campaign_id', label: 'Campaign', options: filters.campaigns.map((campaign) => ({ value: String(campaign.id), label: campaign.name })) },
  { key: 'pledged', label: 'Pledge payment', options: [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }] },
  { key: 'payment_type', label: 'Payment type', options: [{ value: 'card', label: 'Card' }, { value: 'cash', label: 'Cash / offline' }] },
];

export const loadColumnLayout = (storageKey, columnIds) => {
  let visible = DEFAULT_COLUMNS.filter((id) => columnIds.includes(id));
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey));
    if (saved?.visible?.length) visible = saved.visible.filter((id) => columnIds.includes(id));
  } catch (e) {
    console.error('Failed to read General Thank You column layout', e);
  }
  visible = [PINNED_COLUMN, ...visible.filter((id) => id !== PINNED_COLUMN)];
  return { visible, hidden: columnIds.filter((id) => !visible.includes(id)) };
};

export const renderThankYouCell = (id, row, amountFormatter) => {
  if (id === 'donor_name') return row.fan_url ? <a href={row.fan_url}>{row.donor_name}</a> : (row.donor_name ?? '—');
  if (id === 'amount' || id === 'deductible_amount') return amountFormatter.format(Number(row[id]));
  if (id === 'fair_market_value') return row.fair_market_value == null ? '—' : amountFormatter.format(Number(row.fair_market_value));
  if (id === 'pledged') return row.pledged ? 'Yes' : 'No';
  if (id === 'gift_date') return row.gift_date?.slice(0, 10) || '—';
  return row[id] ?? '—';
};

function ReportingDonationThankYouPage({ team, columns, currencyCode }) {
  const {
    isMobile, sidebarOpen, setSidebarOpen,
    dateRange, setDateRange,
    activeFilters, setActiveFilters,
  } = useReportingPage('this_year', { urlFilterIds: FILTER_IDS });

  const basePath = `/teams/${team?.id}/reporting/donations/thank-you`;
  const [filterOptions, setFilterOptions] = useState({ funds: [], campaigns: [] });
  const columnIds = useMemo(() => columns.map((column) => column.id), [columns]);
  const storageKey = `reportingDonationThankYouLayout_${team?.id}`;
  const [columnLayout, setColumnLayout] = useState(() => loadColumnLayout(storageKey, columnIds));
  const [tableSettingsOpen, setTableSettingsOpen] = useState(false);
  const [shownFilters, setShownFilters] = useState(['fund_id', 'giving_campaign_id']);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'gift_date', direction: 'desc' });
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageFor, setPageFor] = useState({ key: null, page: 1 });

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    axios.get(`${basePath}/filter_options`)
      .then((response) => setFilterOptions(response.data))
      .catch((requestError) => console.error('Failed to load General Thank You filter options', requestError));
  }, [basePath]);

  const filterDefinitions = useMemo(() => buildFilterDefinitions(filterOptions), [filterOptions]);
  const amountFormatter = useMemo(() => new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode }), [currencyCode]);

  const apiParams = useMemo(() => ({
    start_date: dateRange?.start || undefined,
    end_date: dateRange?.end || undefined,
    search: search || undefined,
    sort_by: [sort.key],
    sort_direction: [sort.direction],
    ...buildThankYouFilters(activeFilters),
  }), [dateRange, search, sort, activeFilters]);

  const paramsKey = JSON.stringify(apiParams);
  const page = pageFor.key === paramsKey ? pageFor.page : 1;
  const setPage = (next) => setPageFor({ key: paramsKey, page: next });

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setError(null);
    axios.get(`${basePath}/data`, {
      params: { ...apiParams, page, per_page: PER_PAGE },
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'brackets' }),
    })
      .then((response) => {
        if (ignore) return;
        setRows(response.data.donations || []);
        setPagination(response.data.pagination || null);
      })
      .catch((requestError) => {
        if (ignore) return;
        console.error('Failed to load General Thank You report', requestError);
        setError('Unable to load the thank you report. Check the date range and try again.');
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => { ignore = true; };
  }, [basePath, apiParams, page]);

  const exportUrl = `${basePath}/export?${qs.stringify(apiParams, { arrayFormat: 'brackets' })}`;

  const columnsById = Object.fromEntries(columns.map((column) => [column.id, column]));
  const tableColumns = columnLayout.visible.map((id) => ({
    key: id,
    header: columnsById[id].label,
    sortable: columnsById[id].sortable,
    primary: id === PINNED_COLUMN,
    render: (row) => <span style={{ whiteSpace: 'nowrap' }}>{renderThankYouCell(id, row, amountFormatter)}</span>,
  }));
  const toField = (id) => ({ id, label: columnsById[id].label, alwaysVisible: id === PINNED_COLUMN });

  const handleColumnsChange = (visible, hidden) => {
    const ids = visible.map((field) => field.id);
    const layout = {
      visible: [PINNED_COLUMN, ...ids.filter((id) => id !== PINNED_COLUMN)],
      hidden: hidden.map((field) => field.id),
    };
    setColumnLayout(layout);
    try {
      localStorage.setItem(storageKey, JSON.stringify(layout));
    } catch (e) {
      console.error('Failed to save General Thank You column layout', e);
    }
  };

  return (
    <ReportingPageLayout
      team={team}
      activeKey="donations_thank_you"
      title="General Thank You Report"
      subtitle="Gift-level mailing and salutation data. Gift date is the original recorded gift date. Amount follows donation statements after refunds and excludes processing fees."
      dateRange={dateRange}
      setDateRange={setDateRange}
      isMobile={isMobile}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      showFilters={false}
      activeFilters={{}}
      filterGroups={[]}
      filterSidebarOpen={false}
      setFilterSidebarOpen={noop}
      handleFilterChange={noop}
      handleRemoveFilter={noop}
      handleClearAllFilters={noop}
      toolbarExtras={(
        <div className="ml-auto d-flex align-items-center" style={{ gap: 8 }}>
          <div style={{ position: 'relative' }}>
            <Button size="sm" variant="secondary" icon={Columns03} onClick={() => setTableSettingsOpen(!tableSettingsOpen)}>
              Table Settings
            </Button>
            <TableSettings
              isOpen={tableSettingsOpen}
              onClose={() => setTableSettingsOpen(false)}
              visibleFields={columnLayout.visible.map(toField)}
              hiddenFields={columnLayout.hidden.map(toField)}
              onFieldsChange={handleColumnsChange}
            />
          </div>
          <Button size="sm" as="a" href={exportUrl} variant="secondary" icon={Download01}>Export CSV</Button>
        </div>
      )}
    >
      <div className="mb-3">
        <Alert
          color="gray"
          description="This internal report preserves the actual donor. Every enabled contact and organization custom field, such as a salutation or mailing address, can be shown from Table Settings and is always included in the CSV; missing values stay blank. A payment reference is only shown as a check number for a check payment."
        />
      </div>

      <Card padding="none">
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--sl-border-secondary, #e4e7ec)' }}>
          <FilterBar
            variant="plain"
            filters={filterDefinitions}
            values={activeFilters}
            onChange={setActiveFilters}
            shown={shownFilters}
            onShownChange={setShownFilters}
            resultCount={pagination ? `${pagination.total_entries.toLocaleString()} gift${pagination.total_entries === 1 ? '' : 's'}` : undefined}
            search={(
              <TextInput
                size="sm"
                leadingIcon={SearchLg}
                placeholder="Search donor, organization, fund, campaign or reference"
                aria-label="Search gifts"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            )}
          />
        </div>
        <Table
          stickyFirstColumn={!isMobile}
          columns={tableColumns}
          data={rows}
          getRowKey={(row) => row.id}
          loading={loading}
          error={error}
          sort={sort}
          onSortChange={(key, direction) => setSort({ key, direction })}
          empty={<EmptyState size="sm" icon={SearchLg} title="No gifts match these filters." />}
        />
        {pagination?.total_pages > 1 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--sl-border-secondary, #e4e7ec)' }}>
            <Pagination currentPage={page} totalPages={pagination.total_pages} onPageChange={setPage} />
          </div>
        )}
      </Card>
    </ReportingPageLayout>
  );
}

export default ReportingDonationThankYouPage;
